import { Box, Card, Typography } from '@mui/material';
import { observer, useLocalObservable } from 'mobx-react-lite';
import i18n from '../../../i18n';
import TableComponent, { HeadCell, TableRowFeatures } from '../../../component/table/table.component';
import { useCallback, useEffect } from 'react';
import { MobxObjectsManager } from '../../mobx-objs/mobx-manager';
import MainPageState from '../../main/main-page-states';
import SensitiveDishStore, { isSensitiveShiftOperation, PageType } from './sensitive-data-state';
import { DataAnalysis, Order } from '../../../models';
import moment from 'moment';
import dataAnalysisStore from '../data-analysis-state';
import { ErrorMessageBox } from '../data-analysis.page';
import { useTranslation } from 'react-i18next';
import DialogComponent from '../../../component/dialog/dialog.component';
import { ReactComponent as WarningIcon } from '../../../static/images/svg/jam_triangle-danger-f.svg';
import '../sensitive-data/scss/sensitive-data.style.scss';
import PeriodDateFilter from '../../../component/period-date-filter.component';
import { DataExportIconButton } from '../../../component/button/data-export-icon-button.component';
import GlobalNotification from '../../../component/common/snackbar';
import OrderDetailDialog from '../order-data/order-detail-dialog';

/**
 * 操作类型
 */
/** 与 POS summary-critical-log.component 中 operationType 键与 i18n key 保持一致 */
const operationType: Record<number, string> = {
    2: 'payment_text_dish_reprice_message',
    3: 'order_cart_page_text_dish_action_return',
    4: 'manager_text_refund',
    5: 'global_right_sidebar_switch_order_table',
    6: 'global_right_sidebar_merge_order',
    7: 'manager_text_summary_payment_details_refund',
    8: 'global_left_sidebar_manger_hand_over',
    10: 'global_panel_text_discount_money',
    11: 'manager_text_stop_critival_log',
    12: 'payment_text_keyboard_wipe',
    13: 'manager_text_use_coupons',
    14: 'manager_text_bind_vip',
    15: 'table_page_text_dismiss_table_message',
    18: 'order_cart_page_text_history_re_print_info',
    19: 'order_page_text_cancel_order',
    20: 'manager_text_gift_card_redeem_rollback',
    21: 'manager_text_recharge_balance_redeem_rollback',
};

const SensitiveDataPage = observer(({ pageType }: Props) => {
    useTranslation();
    const sensitiveDishStore = useLocalObservable(() => new SensitiveDishStore());
    /** 从主页面状态取当前餐厅，不依赖「当前营业状态」页是否打开 */
    const mainPageData = MobxObjectsManager.get(MainPageState);
    const stringId = mainPageData.currentRestaurant?.string_id || '';
    // 当前时间段筛选
    const startDate = dataAnalysisStore.startDate
    const endDate = dataAnalysisStore.endDate
    useEffect(() => {
        sensitiveDishStore.fetchData(stringId, startDate, endDate, pageType)
    }, [stringId, startDate, endDate, pageType])

    const preCheckExport = useCallback((): boolean => {
        if (!sensitiveDishStore.data?.length) {
            GlobalNotification.warning(i18n.t('general_text_no_data'));
            return false;
        }
        return true;
    }, [sensitiveDishStore.data?.length]);

    const handleExportSensitive = useCallback(() => {
        sensitiveDishStore.downloadExcel(pageType);
    }, [sensitiveDishStore, pageType]);

    /**
     * 与 refund-data 一致：表头排序状态 0=无 1=降序 2=升序
     */
    const getSortStatus = (key: string) => {
        const sort = sensitiveDishStore.sortKeyValue;
        if (key === sort?.key) {
            return sort.value === 'ASC' ? 2 : 1
        }
        return 0
    }

    /**
     * 列头点击：切换 ASC/DESC/取消，在 store 内对 dataSource 做前端排序
     */
    const handleSort = (headCell: HeadCell) => {
        let sortStatus: 'ASC' | 'DESC' | '' = ''
        switch (headCell.sortStatus) {
            case 0:
                sortStatus = 'DESC'
                break
            case 1:
                sortStatus = 'ASC'
                break
            case 2:
                sortStatus = ''
                break
            default:
                sortStatus = ''
                break
        }
        sensitiveDishStore.setSortValue({
            value: sortStatus ? sortStatus : undefined,
            key: sortStatus ? headCell.id : undefined
        })
    }

    /** 交接班：打开交接班 JSON/打印结构详情弹窗（非订单详情组件） */
    const handleOpenShiftDetailDialog = useCallback((value: DataAnalysis) => {
        sensitiveDishStore.setDialogConfig({
            open: true,
            dialogType: value?.order_head_id ? 1 : 2,
            detail: value?.operation_detail || '',
        });
    }, [sensitiveDishStore]);

    /** 非交接班且有关联订单：打开与「订单」页相同的订单详情弹窗（同接口 `get_data_analysis_order_details`） */
    const handleOpenOrderDetailDialog = useCallback(
        (value: DataAnalysis) => {
            const orderHeadId = value.order_head_id;
            if (orderHeadId == null || Number(orderHeadId) <= 0) {
                return;
            }
            const orderStub: Order = { order_head_id: orderHeadId };
            sensitiveDishStore.setOrderDetailData(orderStub);
            sensitiveDishStore.setOpenOrderDetail(true);
            void sensitiveDishStore.fetchDetailData();
        },
        [sensitiveDishStore],
    );

    /** 列表行是否可打开订单详情（有有效订单头 ID） */
    const rowHasOrderDetail = (item: DataAnalysis) => {
        const id = item.order_head_id;
        if (id == null) return false;
        const n = Number(id);
        return !Number.isNaN(n) && n > 0;
    };

    /** 表头列配置（按文案长度设 minWidth，窄屏横向滚动、不挤压列宽） */
    const headCellListService: HeadCell[] = [
        {
            id: 'employee_name',
            label: i18n.t('business_analysis_page_employee_name'),
            minWidth: '160px',
            sortStatus: -1,
        },
        {
            id: 'employee_id',
            label: i18n.t('business_analysis_page_employee_id'),
            minWidth: '120px',
            sortStatus: -1,
        },
        {
            id: 'order_head_id',
            label: i18n.t('business_analysis_page_order_head_id'),
            minWidth: '140px',
            sortStatus: getSortStatus('order_head_id'),
        },
        {
            id: 'operation_time',
            label: i18n.t('business_analysis_page_operation_time'),
            minWidth: '180px',
            sortStatus: getSortStatus('operation_time'),
            renderFunction: (item) => (
                <div>
                    {moment(item.operation_time).format('YYYY-MM-DD HH:mm:ss')}
                </div>
            )
        },
        {
            id: 'operation_name',
            label: i18n.t('business_analysis_page_operation_name'),
            minWidth: '160px',
            sortStatus: getSortStatus('operation_name'),
            renderFunction: (item) => operationType[item.operation_type] ? i18n.t(operationType[item.operation_type]) : item.operation_name
        },
        {
            id: 'operation_detail',
            label: i18n.t('business_analysis_page_operation_detail'),
            minWidth: '240px',
            sortStatus: -1,
            // 交接班不展示正文（与历史打印/弹窗 JSON 列一致）；其它行展示 operation_detail
            renderFunction: (item) => {
                const detail = isSensitiveShiftOperation(item) ? '' : (item.operation_detail ?? '');
                return <div>{detail}</div>;
            },
        },
        {
            id: 'action',
            label: '',
            minWidth: '100px',
            sortStatus: -1,
            // 交接班 → 交接班详情弹窗；有订单号 → 订单详情弹窗；否则无按钮
            renderFunction: (item) => {
                const showDetailButton = isSensitiveShiftOperation(item) || rowHasOrderDetail(item);
                if (!showDetailButton) {
                    return <div />;
                }
                return (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            width: '100%',
                        }}
                    >
                        <TableRowFeatures
                            buttons={[
                                {
                                    action: 'VIEW',
                                    titleText: i18n.t('immediate_check_the_details'),
                                    handleClick: () => {
                                        if (isSensitiveShiftOperation(item)) {
                                            handleOpenShiftDetailDialog(item);
                                        } else {
                                            handleOpenOrderDetailDialog(item);
                                        }
                                    },
                                },
                            ]}
                        />
                    </Box>
                );
            },
        },
    ];

    /**
     * 渲染POS交接班
     */
    const renderDetailContentByPos = (detail: string) => {
        // 脏数据
        if (!detail.includes('{')) {
            return <>{detail}</>
        }
        const data = JSON.parse(detail ?? '{}');
        // pos数据
        return (
            <div className='print-template-box'>
                <div>{data?.head}</div>
                <div>----------------------------------------</div>
                {/* <!-- operation --> */}
                {data?.operation?.map((item: any, index: number) => {
                    return (
                        <div key={`shift-operation-${index}`} className='print-columns'>
                            <span>{item?.operation_cloumn}</span>
                            <span >{item?.operation_value}</span>
                        </div>
                    )
                })}
                <div>----------------------------------------</div>
                {/* <!-- time --> */}
                {data?.time?.map((item: any, index: number) => {
                    return (
                        <div key={`shift-time-${index}`} className='print-columns'>
                            <span>{item?.time_cloumn}</span>
                            <span >{item?.time_value}</span>
                        </div>
                    )
                })}
                <div>----------------------------------------</div>
                {/* <!-- total --> */}
                {data?.total?.map((item: any, index: number) => {
                    return (
                        <div key={`shift-total-${index}`} className='print-columns'>
                            <span>{item?.total_cloumn}</span>
                            <span >{item?.total_value}</span>
                        </div>
                    )
                })}
                <div>----------------------------------------</div>
                {/* <!-- order --> */}
                {data?.order?.map((item: any, index: number) => {
                    return (
                        <div key={`shift-order-${index}`} className='print-columns'>
                            <span>{item?.order_cloumn}</span>
                            <span >{item?.order_value}</span>
                        </div>
                    )
                })}
                <div>----------------------------------------</div>
                {/* <!-- payment  --> */}
                <div>{data?.payment}</div>
                {data?.payments?.map((item: any, index: number) => {
                    return (
                        <div key={`shift-payments-${index}`} className='print-columns'>
                            <span>{item?.payments_cloumn}</span>
                            <span >{item?.payments_value}</span>
                        </div>
                    )
                })}
                {data?.payments_total?.map((item: any, index: number) => {
                    return (
                        <div key={`shift-payments-${index}`} className='print-columns'>
                            <span>{item?.payments_total_cloumn}</span>
                            <span >{item?.payments_total_value}</span>
                        </div>
                    )
                })}
                <div>----------------------------------------</div>
                {/* <!-- Annullation  --> */}
                <div>{data?.annullation}</div>
                {data?.annullations?.map((item: any, index: number) => {
                    return (
                        <div key={`shift-annullations-${index}`} className='print-columns'>
                            <span>{item?.annullations_cloumn}</span>
                            <span >{item?.annullations_value}</span>
                        </div>
                    )
                })}
                <div>----------------------------------------</div>
                {/* <!-- opaned   --> */}
                <div>{data?.opaned}</div>
                {data?.opaneds?.map((item: any, index: number) => {
                    return (
                        <div key={`shift-opaneds-${index}`} className='print-columns'>
                            <span>{item?.opaned_cloumn}</span>
                            <span >{item?.opaned_value}</span>
                        </div>
                    )
                })}
            </div>
        )
    }

    const renderDetailContentByJK = (detail: string) => {
        // 脏数据
        if (!detail.includes('{')) {
            return <>{detail}</>
        }
        const data = JSON.parse(detail ?? '');
        return (
            <div className='print-template-box'>
                {/* <!-- 用餐类型 --> */}
                <div>{`<<   ${i18n.t('business_analysis_page_meal_type')}   >>`}</div>
                {data?.eat_type?.map((item: any, index: number) => {
                    return (
                        <div key={`eat_type-${index}`} className='print-columns'>
                            <span className='print-columns-jk'>{item?.k === 'sum' ? i18n.t('table_component_text_total') : item?.k}</span>
                            <span className='print-columns-jk'>{Number(item?.v).toFixed(2)}</span>
                            <span className='print-columns-jk'>{item?.c}</span>
                        </div>
                    )
                })}
                <br />
                {/* <!-- 结单统计 --> */}
                <div>{`<<   ${i18n.t('business_analysis_page_statement_statistics')}   >>`}</div>
                {data?.check_total?.map((item: any, index: number) => {
                    return (
                        <div key={`check_total-${index}`} className='print-columns'>
                            <span style={{ width: '66%' }}>{item?.k === 'sum' ? i18n.t('table_component_text_total') : item?.k}</span>
                            <span style={{ width: '33%' }}>{Number(item?.v).toFixed(2)}</span>
                        </div>
                    )
                })}
                <br />
                {/* <!-- 支付方式 --> */}
                <div>{`<<   ${i18n.t('business_analysis_page_pattern_of_payment')}   >>`}</div>
                {data?.pays?.map((item: any, index: number) => {
                    return (
                        <div key={`pays-${index}`} className='print-columns'>
                            <span className='print-columns-jk'>{item?.k === 'sum' ? i18n.t('table_component_text_total') : item?.k}</span>
                            <span className='print-columns-jk'>{Number(item?.v).toFixed(2)}</span>
                            <span className='print-columns-jk'>{item?.c}</span>
                        </div>
                    )
                })}
                <br />
                {/* <!-- 钱箱金额 --> */}
                <div>{`<<   ${i18n.t('business_analysis_page_cash_box_amounts')}   >>`}</div>
                {data?.cash?.map((item: any, index: number) => {
                    return (
                        <div key={`cash-${index}`} className='print-columns'>
                            <span className='print-columns-jk'>{item?.k === 'sum' ? i18n.t('table_component_text_total') : item?.k}</span>
                            <span className='print-columns-jk'>{Number(item?.v).toFixed(2)}</span>
                            <span className='print-columns-jk'>{item?.c}</span>
                        </div>
                    )
                })}
                <br />
                {/* <!-- 其它  --> */}
                <div>{`<<   ${i18n.t('global_text_other')}   >>`}</div>
                {data?.other?.map((item: any, index: number) => {
                    return (
                        <div key={`other-${index}`} className='print-columns'>
                            <span style={{ width: '66%' }}>{item?.k}</span>
                            <span style={{ width: '33%' }}>{Number(item?.v).toFixed(2)}</span>
                        </div>
                    )
                })}
            </div>
        )
    }

    /** 渲染标题区域（CLOUD 显示右侧下载/日期操作区） */
    const renderTitleSection = () => (
        <Box className={pageType === 'CLOUD' ? 'sensitive-data-cloud-top-bar' : 'sensitive-data-title-row'}>
            <Box className='sensitive-data-title-row-content'>
                <WarningIcon
                    height={pageType === 'CLOUD' ? 32 : 40}
                    width={pageType === 'CLOUD' ? 32 : 40}
                    style={{ color: 'var(--func-error)', flexShrink: 0 }}
                />
                <Typography component="h2" variant="h3" sx={{ color: 'var(--func-error)' }}>
                    {pageType === 'CLOUD'
                        ? i18n.t('business_analysis_page_employee_sensitive_operation_record')
                        : i18n.t('business_analysis_page_immediate_sensitive_operation_record')}
                </Typography>
            </Box>
            {pageType === 'CLOUD' && (
                <Box className='sensitive-data-cloud-top-actions'>
                    <DataExportIconButton
                        title={i18n.t('tunnel_management_page_export')}
                        disabled={sensitiveDishStore.loading}
                        preCheck={preCheckExport}
                        onExport={handleExportSensitive}
                    />
                    <PeriodDateFilter
                        startDate={dataAnalysisStore.startDate}
                        endDate={dataAnalysisStore.endDate}
                        pickerValue={dataAnalysisStore.pickerValue}
                        onDateTimeChange={dataAnalysisStore.handleDateTime}
                    />
                </Box>
            )}
        </Box>
    )

    return (
        sensitiveDishStore.errorMessage
            ? <ErrorMessageBox errorMessage={sensitiveDishStore.errorMessage} />
            :
            <Box className={pageType === 'CLOUD' ? 'sensitive-data-page-cloud' : 'sensitive-data-page-instant'}>
                <Card
                    className={pageType === 'CLOUD' ? 'sensitive-data-card-cloud' : 'sensitive-data-card-instant'}
                    sx={{
                        margin: pageType === 'CLOUD' ? '16px 0 0' : '0',
                        padding: pageType === 'CLOUD' ? '24px 16px 16px' : '16px',
                        boxShadow: pageType === 'CLOUD' ? '0px 0px 4px 0px var(--overlay-800-a25)' : '0px 0px 4px 0px var(--shadow-25)',
                        display: 'flex',
                        flexDirection: 'column',
                        flex: 1,
                        minHeight: 0,
                    }}
                >
                    {/* 标题区：CLOUD 为顶部栏+操作区，INSTANT 保持原始标题样式 */}
                    {renderTitleSection()}
                    <TableComponent
                        extraClass={pageType === 'CLOUD' ? 'sensitive-data-cloud-table' : 'sensitive-data-instant-table'}
                        // 分页
                        isPagination
                        allCount={sensitiveDishStore.data?.length}
                        // 列表项
                        headCellList={headCellListService}
                        // 列表数据
                        list={sensitiveDishStore.data}
                        // 是否显示复选框 0 不显示 1显示  2 显示但是不显示删除提示框
                        showChecked={0}
                        // 是否显示加载框 0不开启
                        isTableLoading={sensitiveDishStore.loading}
                        // 排序方法（前端排序，同 refund-data）
                        handleSort={handleSort}
                    />
                    {sensitiveDishStore.dialogConfig.open && (
                        <DialogComponent
                            fullWidth
                            heightSize={'large'}
                            defaultHeaderProps={{
                                title: i18n.t('general_text_details'),
                                onClose: () => sensitiveDishStore.setDialogConfig({
                                    open: false,
                                    dialogType: undefined
                                }),
                            }}
                            maxWidth={'xs'}
                            open={sensitiveDishStore.dialogConfig.open}
                            onClose={() => sensitiveDishStore.setDialogConfig({
                                open: false,
                                dialogType: undefined
                            })}
                        >
                            <Box sx={{ padding: '16px',background: 'var(--fill-neutral-100)',borderRadius: '8px' }}>
                                {
                                    sensitiveDishStore.dialogConfig.dialogType === 1 ?
                                        renderDetailContentByJK(sensitiveDishStore.dialogConfig.detail ?? '')
                                        : renderDetailContentByPos(sensitiveDishStore.dialogConfig.detail ?? '')
                                }
                            </Box>
                        </DialogComponent>
                    )}
                    <OrderDetailDialog
                        handleClose={() => sensitiveDishStore.setOpenOrderDetail(false)}
                        open={sensitiveDishStore.isOpenOrderDetail}
                        order={sensitiveDishStore.orderDetailSummary}
                        list={sensitiveDishStore.orderDetailDish}
                        isTableLoading={sensitiveDishStore.detailLoading}
                    />
                </Card>
            </Box>
    )
})

export default SensitiveDataPage

type Props = {
    // 页面：退菜详情、即时退菜详情
    pageType: PageType
}